import { useState, useEffect } from 'react';
import { CONFIG, Dialog, api, useToast } from './_barrel.js';

        // Platform codes match the add-handle select on the detail page
        // (EditSubjectDialog) so handles seeded here look identical to ones
        // added later. Unknown hosts and bare names fall back to "other".
        const HANDLE_PLATFORM_HOSTS = [
            [/(^|\.)(twitter|x)\.com$/, 'twitter'],
            [/(^|\.)instagram\.com$/, 'instagram'],
            [/(^|\.)tiktok\.com$/, 'tiktok'],
            [/(^|\.)(facebook|fb)\.com$/, 'facebook'],
            [/(^|\.)linkedin\.com$/, 'linkedin'],
            [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
            [/(^|\.)reddit\.com$/, 'reddit'],
            [/(^|\.)(t\.me|telegram\.(me|org))$/, 'telegram'],
            [/(^|\.)threads\.net$/, 'threads'],
            [/(^|\.)bsky\.app$/, 'bluesky'],
        ];
        const HANDLE_PLATFORM_CODES = new Set([
            ...HANDLE_PLATFORM_HOSTS.map(([, code]) => code), 'other',
        ]);
        // Path prefixes that sit before the username on some platforms
        // (linkedin.com/in/name, reddit.com/u/name, youtube.com/c/name).
        const HANDLE_PATH_PREFIXES = new Set(['in', 'u', 'user', 'c', 'channel', 'profile']);

        // "Also known as": comma-separated, trimmed, empties and repeats removed.
        function parseAliases(text) {
            const seen = new Set();
            const out = [];
            for (const raw of String(text || '').split(/[,\n]/)) {
                const alias = raw.trim();
                if (!alias) continue;
                const key = alias.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                out.push({ alias, alias_type: null });
            }
            return out;
        }

        // One handle string -> { platform, handle, url } as the create API
        // expects. Accepts "@name", "name", "platform:name" or a profile URL.
        function parseHandle(raw) {
            const s = raw.trim();
            if (!s) return null;
            // Profile URL, with or without a scheme (x.com/name, www.instagram.com/name).
            const looksLikeUrl = /^https?:\/\//i.test(s)
                || /^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+\/\S*/i.test(s);
            if (looksLikeUrl) {
                let u;
                try { u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`); } catch (_) { u = null; }
                if (u) {
                    const host = u.hostname.toLowerCase();
                    const match = HANDLE_PLATFORM_HOSTS.find(([re]) => re.test(host));
                    const platform = match ? match[1] : 'other';
                    const segs = u.pathname.split('/').filter(Boolean);
                    while (segs.length > 1 && HANDLE_PATH_PREFIXES.has(segs[0].toLowerCase())) segs.shift();
                    // Known platforms put the username first; for anything
                    // else the last path segment is the better guess.
                    let handle = ((match ? segs[0] : segs[segs.length - 1]) || '').replace(/^@/, '');
                    // facebook.com/profile.php?id=123 — the id is the handle.
                    if (!handle || handle === 'profile.php') handle = u.searchParams.get('id') || handle || host;
                    return { platform, handle: decodeURIComponent(handle), url: u.toString() };
                }
            }
            // "twitter:name" / "instagram: @name" — explicit platform.
            const explicit = s.match(/^([a-z]+)\s*:\s*@?(\S+)$/i);
            if (explicit) {
                const code = explicit[1].toLowerCase();
                const platform = code === 'x' ? 'twitter' : (HANDLE_PLATFORM_CODES.has(code) ? code : 'other');
                return { platform, handle: explicit[2], url: null };
            }
            // Bare "@name" or "name".
            return { platform: 'other', handle: s.replace(/^@/, ''), url: null };
        }

        // "Online handles": one per line or comma-separated, de-duplicated on
        // (platform, handle) to match the API's unique constraint.
        function parseHandles(text) {
            const seen = new Set();
            const out = [];
            for (const raw of String(text || '').split(/[,\n]/)) {
                const h = parseHandle(raw);
                if (!h || !h.handle) continue;
                const key = `${h.platform}:${h.handle.toLowerCase()}`;
                if (seen.has(key)) continue;
                seen.add(key);
                out.push(h);
            }
            return out;
        }

        function NewSubjectDialog({ token, onClose, onSuccess }) {
            const [form, setForm] = useState({
                subject_type_code: 'principal',
                full_name: '',
                display_name: '',
                lawful_basis: 'legitimate_interests',
                processing_purpose: '',
                lia_reference: '',
                client_tag: '',
                is_minor: false,
                year_of_birth: '',
            });
            // Free-text seeds for the first collection sweep; parsed on submit.
            const [aliasText, setAliasText] = useState('');
            const [handleText, setHandleText] = useState('');
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            const [duplicates, setDuplicates] = useState([]);
            const [duplicateOverride, setDuplicateOverride] = useState(false);
            // Optional adtech-exposure seed file, analysed right after creation.
            const [locationFile, setLocationFile] = useState(null);
            const toast = useToast();

            // Debounced duplicate check — fires 600ms after the user stops
            // typing into the full_name field so we don't hammer the API on
            // every keystroke.
            useEffect(() => {
                const name = form.full_name.trim();
                if (name.length < 3) {
                    setDuplicates([]);
                    setDuplicateOverride(false);
                    return;
                }
                const handle = setTimeout(async () => {
                    try {
                        const params = new URLSearchParams({ full_name: name });
                        const yob = parseInt(form.year_of_birth, 10);
                        if (!isNaN(yob) && yob >= 1900 && yob <= 2100) {
                            params.set('year_of_birth', String(yob));
                        }
                        const r = await api(`/api/v1/subjects/check-duplicate?${params}`, {}, token);
                        setDuplicates(r.candidates || []);
                        // If candidates list changes, require an override again
                        if ((r.candidates || []).length === 0) {
                            setDuplicateOverride(false);
                        }
                    } catch (e) { /* silent — duplicate check is non-blocking */ }
                }, 600);
                return () => clearTimeout(handle);
            }, [form.full_name, form.year_of_birth, token]);

            const submit = async (e) => {
                e.preventDefault();
                // Block submit if duplicates were found and override hasn't been chosen
                if (duplicates.length > 0 && !duplicateOverride) {
                    setError('Possible duplicate found. Tick "Add anyway" to proceed.');
                    return;
                }
                const aliases = parseAliases(aliasText);
                const handles = parseHandles(handleText);
                // API limits: alias and handle strings are capped at 255 chars.
                const tooLong = aliases.find(a => a.alias.length > 255)?.alias
                    || handles.find(h => h.handle.length > 255)?.handle;
                if (tooLong) {
                    setError(`"${tooLong.slice(0, 40)}…" is longer than 255 characters. Shorten it or remove it.`);
                    return;
                }
                setSubmitting(true);
                setError(null);
                try {
                    const body = { ...form, aliases, handles };
                    if (!body.display_name) body.display_name = null;
                    if (!body.lia_reference) body.lia_reference = null;
                    if (!body.client_tag) body.client_tag = null;
                    if (!body.year_of_birth) body.year_of_birth = null;
                    else body.year_of_birth = parseInt(body.year_of_birth, 10);
                    const created = await api('/api/v1/subjects', { method: 'POST', body: JSON.stringify(body) }, token);
                    toast('Subject created');
                    // Optional: seed adtech exposure from an uploaded location file.
                    // Non-fatal — the subject exists regardless of the analysis.
                    if (locationFile && created && created.id) {
                        try {
                            const fd = new FormData();
                            fd.append('file', locationFile);
                            fd.append('label', locationFile.name);
                            const resp = await fetch(`${CONFIG.apiBase}/api/v1/aea/subjects/${created.id}/trial`,
                                { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                            if (resp.ok) {
                                const d = await resp.json();
                                const g = d.analysis && d.analysis.exposure && d.analysis.exposure.grade;
                                toast(g ? `Exposure analysed — grade ${g}` : 'Location file analysed');
                            } else {
                                let m = `Exposure upload failed (${resp.status})`;
                                try { const j = await resp.json(); m = j.detail || m; } catch (_) {}
                                toast(m);
                            }
                        } catch (ex) {
                            toast('Subject created, but the location file could not be analysed: ' + ex.message);
                        }
                    }
                    onSuccess?.();
                    onClose();
                } catch (err) {
                    setError(err.message);
                } finally {
                    setSubmitting(false);
                }
            };

            return (
                <Dialog onClose={onClose} title="Add Subject">
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label>Subject Type</label>
                            <select value={form.subject_type_code} onChange={e => setForm({...form, subject_type_code: e.target.value, is_minor: e.target.value === 'child'})}>
                                <option value="principal">Principal</option>
                                <option value="family_member">Family member</option>
                                <option value="presenter">Presenter</option>
                                <option value="executive">Executive</option>
                                <option value="staff">Staff</option>
                                <option value="child">Child (Minor)</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Full Name *</label>
                                <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required />
                            </div>
                            <div>
                                <label>Display Name</label>
                                <input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Year of Birth (optional, used for disambiguation)</label>
                                <input type="number" min="1900" max="2100" value={form.year_of_birth}
                                    onChange={e => setForm({...form, year_of_birth: e.target.value})}
                                    placeholder="e.g. 1972" />
                            </div>
                        </div>

                        <div>
                            <label>Also known as (optional)</label>
                            <input value={aliasText} onChange={e => setAliasText(e.target.value)}
                                   autoComplete="off" autoCapitalize="words"
                                   placeholder="e.g. Jon Smith, J. Smith, maiden or stage name" />
                            <div className="text-xs text-muted mt-1">
                                Comma-separated. The first collection sweep searches these names as well as the full name.
                            </div>
                        </div>
                        <div>
                            <label>Online handles (optional)</label>
                            <textarea rows="3" value={handleText} onChange={e => setHandleText(e.target.value)}
                                      autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck="false"
                                      placeholder={'@name\ninstagram:name\nhttps://www.linkedin.com/in/name'}></textarea>
                            <div className="text-xs text-muted mt-1">
                                One per line or comma-separated: <span className="font-mono">@name</span>, <span className="font-mono">platform:name</span> or
                                a profile URL (platform is read from the link). The first sweep watches these accounts; you can edit them on the record.
                            </div>
                        </div>

                        {duplicates.length > 0 && (
                            <div className="p-3" style={{background:'rgba(216,154,74,0.10)', border:'1px solid var(--amber)'}}>
                                <div className="font-mono text-xs uppercase tracking-widest mb-2" style={{color:'var(--amber)'}}>
                                    Possible duplicate{duplicates.length > 1 ? 's' : ''} found
                                </div>
                                <div className="text-xs text-muted mb-3">
                                    {duplicates.length === 1 ? 'A subject already exists' : `${duplicates.length} subjects already exist`} with a similar name.
                                    Review before adding a new record.
                                </div>
                                <div className="space-y-2">
                                    {duplicates.map(d => (
                                        <div key={d.id} className="text-sm py-1 border-b border-veil last:border-b-0">
                                            <div>{d.display_name || d.full_name}</div>
                                            <div className="text-xs text-muted font-mono">
                                                {d.full_name}
                                                {d.year_of_birth ? ` · born ${d.year_of_birth}` : ''}
                                                {' · '}
                                                {d.subject_type_code.replace('_', ' ')}
                                                {' · '}similarity {Math.round(d.similarity * 100)}%
                                            </div>
                                            <div className="text-xs text-muted">{d.match_reason}</div>
                                        </div>
                                    ))}
                                </div>
                                <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer">
                                    <input type="checkbox" checked={duplicateOverride}
                                        onChange={e => setDuplicateOverride(e.target.checked)} />
                                    <span>Add anyway — these are different people</span>
                                </label>
                            </div>
                        )}

                        <div>
                            <label>Lawful Basis</label>
                            <select value={form.lawful_basis} onChange={e => setForm({...form, lawful_basis: e.target.value})}>
                                <option value="legitimate_interests">Legitimate interests</option>
                                <option value="consent">Consent</option>
                                <option value="contract">Contract</option>
                                <option value="legal_obligation">Legal obligation</option>
                                <option value="vital_interests">Vital interests</option>
                                <option value="public_task">Public task</option>
                            </select>
                        </div>
                        <div>
                            <label>Processing Purpose *</label>
                            <textarea rows="3" value={form.processing_purpose} onChange={e => setForm({...form, processing_purpose: e.target.value})} required
                                placeholder="e.g. Protective intelligence monitoring under signed engagement agreement"></textarea>
                        </div>
                        <div>
                            <label>LIA Reference (recommended for legitimate interests)</label>
                            <input value={form.lia_reference} onChange={e => setForm({...form, lia_reference: e.target.value})} placeholder="LIA-2026-XXX" />
                        </div>
                        <div>
                            <label>Client / Engagement (optional)</label>
                            <input value={form.client_tag} onChange={e => setForm({...form, client_tag: e.target.value})}
                                   placeholder="e.g. Purple Protection — A1 family 2026" />
                            <div className="text-xs text-muted mt-1">Free-text label so you can group subjects by client.</div>
                        </div>
                        {form.subject_type_code === 'child' && (
                            <div className="p-3 border border-[var(--amber)] text-xs text-[var(--amber)]">
                                Child subjects require additional fields (DPIA reference, minor_until_date, guardian consent if applicable). Use the full API for now — this quick form supports adult subjects only.
                            </div>
                        )}
                        <div>
                            <label>Location exposure file (optional)</label>
                            <input type="file" accept=".json,.csv,.xlsx,.gpx,.zip,.jpg,.jpeg,.heic,.tif,.tiff"
                                   onChange={e => setLocationFile((e.target.files && e.target.files[0]) || null)}
                                   style={{ width: '100%', color: 'var(--text)', fontSize: '13px' }} />
                            <div className="text-xs text-muted mt-1">
                                Optional. Analysed on creation to seed this subject's adtech exposure (Home/Work
                                inference, predictability, grade). Requires the subject's consent to process.
                            </div>
                            <div className="text-xs text-muted mt-1">
                                Accepts a Google Timeline export, a Takeout <span className="font-mono">Records.json</span>,
                                a fitness <span className="font-mono">.gpx</span> track, geotagged photos
                                (<span className="font-mono">.zip</span> or a single image), or a CSV/JSON of positions.
                                <span className="gold"> Timeline switched off? Photos or a fitness track work just as well</span>
                                — see the client onboarding pack.
                            </div>
                        </div>
                        {error && <div className="text-[var(--red)] text-sm">{error}</div>}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-gold" disabled={submitting || form.subject_type_code === 'child'}>
                                {submitting ? (locationFile ? 'Creating & analysing…' : 'Creating…') : 'Create Subject'}
                            </button>
                        </div>
                    </form>
                </Dialog>
            );
        }

export { NewSubjectDialog };
